import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, App as AntdApp, Modal, Typography } from 'antd'
import { useTranslation } from 'react-i18next'

import {
  organizationsAccessDisableMfaCreate,
  organizationsAccessRequireMfaCreate,
  organizationsAccessResetMfaCreate,
  organizationsAccessResetPasswordCreate,
  organizationsAccessRevokeInviteCreate,
  organizationsOrganizationsRetrieveOptions
} from 'src/api/main'
import { buildWorkspacePasswordResetUrl } from 'src/features/workspaces/utils/inviteUrl'
import { useShowApiErrors } from 'src/utils'

import { invalidateWorkspaceUserQueries } from '../hooks'
import type { WorkspaceUser } from '../types'
import { composeDisplayName } from '../utils'

const { Text } = Typography

/** Simple confirm-and-mutate lifecycle actions (impersonate has its own modal). */
export type ConfirmAction =
  | 'revokeInvite'
  | 'resetPassword'
  | 'requireMfa'
  | 'disableMfa'
  | 'resetMfa'

type Props = {
  workspaceId: number
  open: boolean
  action: ConfirmAction
  user: WorkspaceUser | null
  onClose: () => void
}

export const UserConfirmActionModal = ({
  workspaceId,
  open,
  action,
  user,
  onClose
}: Props) => {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const qc = useQueryClient()
  const showApiErrors = useShowApiErrors()

  // Reset-password needs the workspace domain to build the email link target.
  const { data: organization } = useQuery({
    ...organizationsOrganizationsRetrieveOptions({ path: { id: workspaceId } }),
    enabled: open && action === 'resetPassword' && Number.isFinite(workspaceId)
  })

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('No member selected')
      if (action === 'revokeInvite') {
        await organizationsAccessRevokeInviteCreate({
          path: { id: user.id },
          throwOnError: true
        })
        return
      }
      if (action === 'requireMfa') {
        await organizationsAccessRequireMfaCreate({
          path: { id: user.id },
          throwOnError: true
        })
        return
      }
      if (action === 'disableMfa') {
        await organizationsAccessDisableMfaCreate({
          path: { id: user.id },
          throwOnError: true
        })
        return
      }
      if (action === 'resetMfa') {
        await organizationsAccessResetMfaCreate({
          path: { id: user.id },
          throwOnError: true
        })
        return
      }
      // resetPassword
      if (!organization?.domain_name) {
        throw new Error('Workspace domain not ready')
      }
      await organizationsAccessResetPasswordCreate({
        path: { id: user.id },
        body: {
          redirect_url: buildWorkspacePasswordResetUrl(organization.domain_name)
        },
        throwOnError: true
      })
    },
    onSuccess: async () => {
      message.success(t(`users.lifecycle.${action}.success` as const))
      if (user) {
        await invalidateWorkspaceUserQueries(qc, {
          workspaceId,
          organizationAccessId: user.id,
          userId: user.user.id
        })
      }
      onClose()
    },
    onError: (e) => showApiErrors(e)
  })

  const name = user
    ? composeDisplayName(
        user.user.first_name,
        user.user.last_name,
        user.user.email
      )
    : ''

  const needsDomain = action === 'resetPassword' && !organization?.domain_name

  return (
    <Modal
      open={open}
      centered
      title={t(`users.lifecycle.${action}.title` as const)}
      okText={t(`users.lifecycle.${action}.submit` as const)}
      cancelText={t('users.modals.cancel')}
      okType={action === 'revokeInvite' ? 'danger' : 'primary'}
      okButtonProps={{ loading: isPending, disabled: needsDomain }}
      cancelButtonProps={{ disabled: isPending }}
      onOk={() => void mutateAsync()}
      onCancel={() => {
        if (!isPending) onClose()
      }}
      destroyOnHidden
    >
      <p style={{ marginTop: 0, color: 'var(--ant-color-text-secondary)' }}>
        {t(`users.lifecycle.${action}.description` as const, { name })}
      </p>
      {action === 'resetPassword' || action === 'requireMfa' ? (
        <p style={{ margin: '4px 0 0' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('users.modals.resendInvite.recipient')}{' '}
          </Text>
          <Text strong>{user?.user.email ?? '—'}</Text>
        </p>
      ) : null}
      {needsDomain ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 12 }}
          title={t('users.invite.error.workspaceNotReady')}
        />
      ) : null}
    </Modal>
  )
}
